import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Youtube, Mail, Phone, MapPin } from 'lucide-react';

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const Footer = () => {
  return (
    <footer className="footer-bg border-t border-saffron-100" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-saffron-500 to-gold-500 rounded-full flex items-center justify-center">
                <span className="text-white font-heading font-bold text-lg">ੴ</span>
              </div>
              <span className="font-heading font-bold text-xl text-royal-600">Khalsa Sidak</span>
            </div>
            <p className="text-gray-600 text-sm leading-relaxed">
              A dedicated digital platform and Sikh accessories shop designed to support the Sikh community with learning, 
              guidance, spiritual updates, and authentic religious items. Promoting knowledge, discipline, and unity through modern technology.
            </p>
            <p className="text-saffron-600 font-medium text-sm">
              ਵਾਹਿਗੁਰੂ ਜੀ ਕਾ ਖਾਲਸਾ, ਵਾਹਿਗੁਰੂ ਜੀ ਕੀ ਫਤਿਹ
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold text-lg text-royal-600 mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {[
                { to: '/teachers', label: 'Find Teachers' },
                { to: '/katha', label: 'Listen to Katha' },
                { to: '/live-kirtan', label: 'Live Kirtan' },
                { to: '/courses', label: 'Sikh School' },
                { to: '/shop', label: 'Shop' },
                { to: '/directory', label: 'Local Directory' },
              ].map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-gray-600 hover:text-saffron-600 text-sm transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="font-heading font-semibold text-lg text-royal-600 mb-4">Resources</h4>
            <ul className="space-y-2">
              {[
                { to: '/events', label: 'Community Events' },
                { to: '/collaborate', label: 'Collaborate With Us' },
                { to: '/advertise', label: 'Advertise With Us' },
                { to: '/chatbot', label: 'Ask AI Assistant' },
                { to: '/about', label: 'About Sikhism' },
                { to: '/contact', label: 'Contact Us' },
              ].map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-gray-600 hover:text-saffron-600 text-sm transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-semibold text-lg text-royal-600 mb-4">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-2 text-gray-600 text-sm">
                <Mail className="w-4 h-4 text-saffron-500" />
                <a href="mailto:Khalsasidak13@gmail.com" className="hover:text-saffron-600">Khalsasidak13@gmail.com</a>
              </li>
              <li className="flex items-center gap-2 text-gray-600 text-sm">
                <Phone className="w-4 h-4 text-saffron-500" />
                <a href="tel:+12404747537" className="hover:text-saffron-600">240-474-7537</a>
              </li>
              <li className="flex items-start gap-2 text-gray-600 text-sm">
                <MapPin className="w-4 h-4 text-saffron-500 mt-0.5" />
                <span>Global Sikh Community</span>
              </li>
            </ul>
            
            {/* Social Links */}
            <div className="flex gap-3 mt-4">
              {[
                { icon: Facebook, href: 'https://www.facebook.com/search/top?q=sikh%20sangat', label: 'Facebook', color: 'hover:bg-blue-600' },
                { icon: Instagram, href: 'https://www.instagram.com/explore/tags/sikhcommunity', label: 'Instagram', color: 'hover:bg-gradient-to-r hover:from-purple-500 hover:via-pink-500 hover:to-orange-500' },
                { icon: Youtube, href: 'https://www.youtube.com/results?search_query=sikh+katha+kirtan', label: 'YouTube', color: 'hover:bg-red-600' },
                { icon: TikTokIcon, href: 'https://www.tiktok.com/search?q=sikh', label: 'TikTok', color: 'hover:bg-black' },
                { icon: Twitter, href: 'https://twitter.com/search?q=sikh&src=typed_query', label: 'Twitter', color: 'hover:bg-sky-500' },
              ].map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={social.label}
                  className={`w-9 h-9 bg-white border border-saffron-200 rounded-full flex items-center justify-center text-gray-600 ${social.color} hover:text-white hover:border-transparent transition-all`}
                >
                  <social.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-6 border-t border-saffron-100 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} Khalsa Sidak. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link to="/privacy" className="text-gray-500 hover:text-saffron-600 text-sm transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-gray-500 hover:text-saffron-600 text-sm transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
